/* Email Provider status panel.
 *
 * 2026-02 — Cloudflare outbound was retired because Cloudflare Email
 * Routing is INBOUND-ONLY (Workers Email Bindings for outbound never
 * left beta) and the presence of a "cloudflare" branch caused an
 * IT-console flag: "MX handler looking for Cloudflare for send
 * functions". Resend is now the single canonical outbound path.
 *
 * This component is intentionally read-only now — no toggle, just a
 * clear status pill so admins know exactly what's happening. The
 * previous DB-backed provider selection endpoint still exists but
 * only accepts "resend" (validator in routes_admin.py).
 */
import React from 'react';
import { Mail, Zap, ShieldCheck } from 'lucide-react';

export default function EmailProviderSelector({ cfg }) {
  const enabled = cfg?.integrations_enabled?.resend !== false;
  return (
    <div
      className="mt-4 rounded-md border border-border bg-card p-4"
      data-testid="admin-email-provider-selector"
    >
      <div className="flex items-start gap-2">
        <Mail className="mt-0.5 h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] font-semibold">Outbound email provider</span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider ${enabled ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300'}`}
              data-testid="admin-email-provider-active"
            >
              <Zap className="h-3 w-3" /> {enabled ? 'Resend on' : 'Resend disabled'}
            </span>
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            All transactional email (receipts, password rotations, notebook
            digests, welcome mail, reader alerts) is sent through <b>Resend</b>.
            The Cloudflare option was retired on 2026-02 — Cloudflare Email
            Routing is inbound-only, so it never belonged on the outbound path.
          </div>
          {!enabled && <div className="mt-2 text-[11px] font-semibold text-rose-700 dark:text-rose-300" data-testid="admin-email-provider-disabled-note">Turn on Resend in the Integrations switch directly below.</div>}
        </div>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-sm border border-dashed border-border bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div>
          <b className="text-foreground">Inbound still flows through Cloudflare</b> —
          `crownnrose.com` MX record points to Cloudflare Email Routing, which
          forwards every message to our `/api/webhooks/email-inbound` handler.
          That path is unchanged; only outbound moved fully to Resend.
        </div>
      </div>
    </div>
  );
}
